import { storefront } from '../client';
import { flattenMetobjectArrayResponse } from '../../utilities/storefront-api/flattenMetaobjects';
import { addBaseGid } from '../../utilities/handleGid';
import { logError, logWarning } from '../../utilities/logging';

const getMetaobjectsByIdsQuery = `
query getMetaobjectsByIds($ids: [ID!]!, $countryCode: CountryCode)
 @inContext(country: $countryCode) {
  nodes(ids: $ids) {
    ... on Metaobject {
      id
      handle
      fields {
        key
        value
        reference {
          ... on Page {
            title
            body
            modalTriggerText: metafield(namespace: "$app:modal", key: "triggerText") {
              value
            }
          }
          ... on MediaImage {
            id
            image {
              url
              altText
              width
              height
            }
          }
        }
        productVariants: references(first: 250) {
          nodes {
            ... on ProductVariant {
              id
              title
              price {
                amount
                currencyCode
              }
              image {
                url
                altText
              }
              product {
                id
                title
                handle
              }
            }
          }
        }
      }
    }
  }
}`;

/**
 * Fetches multiple metaobjects by their IDs in a single request.
 * @param {Object} options - Options object.
 * @param {string} options.country - The country code for the request.
 * @returns {Function} Function that takes ids and metaObjectType and returns Promise<Object[]> - Array of flattened metaobject objects.
 */
export const fetchMetaobjectsByIds = ({ country }) =>
  //* @returns {Promise<Object[]>} Array of flattened metaobject objects.
  async ({ ids, metaObjectType }) => {
    // Ensure all IDs are in GID format
    const metaobjectGids = ids.filter((id) => id.startsWith(addBaseGid({ dataType: 'Metaobject' })));

    if (metaobjectGids.length < ids.length) {
      const skippedIds = ids.filter((id) => !id.startsWith(addBaseGid({ dataType: 'Metaobject' })));
      logWarning({
        label: 'fetchMetaobjectsByIds',
        warning: 'Some metaobject IDs were not in GID format',
        message: 'Skipping metaobject IDs that are not in GID format',
        extraData: { skippedIds, metaObjectType }
      });
    }

    const { data, errors } = await storefront.request(getMetaobjectsByIdsQuery, {
      variables: { ids: metaobjectGids, country }
    });

    if (errors) {
      logError({
        label: 'fetchMetaobjectsByIds',
        error: new Error('GraphQL errors occurred'),
        message: 'Error gathering metaobject data for metaobject IDs',
        extraData: { metaObjectType, ids, errors }
      });
      throw new Error(`Error gathering metaobject data for these ${metaObjectType} metaobject IDs: ${ids.join(', ')}`);
    }

    // Filter out nulls and non-metaobject nodes
    const metaobjects = (data?.nodes || []).filter((item) => item !== null);
    return flattenMetobjectArrayResponse({ metaobjects });
  };
